/**
 * interpolation.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import * as moment from "moment";
import { StdVariable, Variable } from "../../aggregation/typed-parameter";
import { CollectionType, DataType } from "../../common/data/data-types";
import { empty } from "../../support/utilities/functions";
import { English } from "../../support/utilities/language-helpers";

import {
    DefaultDirective,
    Directive,
    DirectiveClass,
    DirectiveEntry,
    Directives,
    PassThroughDirective,
    wrap
} from "./interpolation-directives";
import { TypedData, TypedDataHolder } from "app/common/data/typed-data";

type AnyDirective = Directive<Variable<any>, any, any>;

type Interpolator<T> = (variables : Array<Variable<any>>) => T;

/**
 * The shape of the presentation property on presentable AlgorithmSteps, as it
 * arrives from the server. This is transformed into a string by the functions
 * below as soon as it is validated, so the step + message classes simply denote
 * the presentation property as a string.
 */
export interface TemplatedPresentation
{
    template : string;
    values : StdVariable[];
}
interface Presentable
{
    presentation : TemplatedPresentation | string;
}

/**
 * HTML string to insert for null values.
 */
const NULL_FOR_HUMANS : string = "<code>(unknown)</code>";

function dumpInterpolation (template : string, values : StdVariable[], htmlNewLines : boolean = true) : string
{
    let s : string = "";
    values.forEach((v : StdVariable) => {
        s += (htmlNewLines ? "<br>" : "\n") + `${v.displayName || v.name}: `;
        if (v.value)
        {
            s += TypedData.toString(v as TypedDataHolder);
        }
    });
    return (s + (htmlNewLines ? "<br>" : "\n") + "template: " + template);
}

/**
 * Replace a presentation object with its interpolated string form.
 * @param obj an object with an interpolable presentation
 */
export function interpolateInPlace (obj : Presentable)
    : void
{
    if (typeof obj.presentation === "object")
    {
        try {
            obj.presentation = interpolateString(
                obj.presentation.template,
                obj.presentation.values
            );
        }
        catch (err)
        {
            obj.presentation = dumpInterpolation(
                obj.presentation.template,
                obj.presentation.values);
        }
    }
}

/**
 * Interpolate an array of variables into binding sites in a template.
 * @param template The template string
 * @param variables Array of variable to fill into the template
 */
export function interpolateString (template : string, variables : StdVariable[])
    : string
{
    try
    {
        return buildInterpolator(template, variables)(variables);
    }
    catch (err)
    {
        if (err instanceof TypeError)
        {
            throw err;
        }
        return `<code>Interpolator failure: ${err}</code>`;
    }
}

enum TextState
{
    Normal,
    VariableName,
    DirectiveName,
    SawTick,
    Saw$
}

/**
 * A {@link ParseChunk} is a tree node representing a piece of the parsed
 * string interpolation template. This tree forms an intermediate representation
 * between the template string and the final interpolator function.
 */
abstract class ParseNode
{
    /**
     * During parse, the elements being accumulated into the node.
     * After parse, the child nodes of the node that don't have any special
     * roles.
     */
    public content : ParseNode[] = [];

    /**
     * Finish parsing and eject an interpolator function from the tree's
     * intermediate representation. Non-root nodes may also return a string,
     * indicating that the entire subtree's interpolation result is statically
     * calculable.
     */
    // public abstract eject () : Chunk[];

    /**
     *
     */
    public addString (str : string) : void
    {
        if (str === "")
        {
            return;
        }
        this.content.push(new StringNode(str));
    }

    public add (node : ParseNode) : ParseNode
    {
        this.content.push(node);
        return node;
    }

    /**
     *
     */
    public finish () : void
    {
        //
    }

    /**
     * Canonize and validate the current partial as the left-hand-side of a
     * binding site (i.e. a variable name or a string input)
     */
    public finishTarget (variables : Array<Variable<any>>) : void
    {
        throw new Error(`Cannot add target to non-binding-site`);
    }

    public finishParameter () : boolean
    {
        throw new Error(`Arbitrary nodes do not have parameters`);
    }

    public replaceLast (node : ParseNode) : ParseNode
    {
        if (this.content.length === 0)
        {
            throw new Error(`Cannot replace node; node has no content`);
        }
        this.content.splice(-1, 1, node);
        return node;
    }

    public setDirective (name : string, required : boolean = false)
        : boolean
    {
        throw new Error(`Cannot add directive to non-binding-site`);
    }

    public abstract returnType () : DataType;

    public returnCardinality () : number
    {
        return 1;
    }

    public abstract eject () : Interpolator<Variable<any>>;
}

class StringNode extends ParseNode
{
    constructor (public value : string)
    {
        super();
    }

    public returnType () : "parseable"
    {
        return "parseable";
    }

    public eject () : Interpolator<Variable<"parseable">>
    {
        return () => wrap("parseable", this.value);
    }
}

class ReferenceNode extends ParseNode
{
    public variableName : string;
    private type : DataType;
    private cardinality : number;
    private collectionType : CollectionType;

    public returnCardinality () : number
    {
        return this.cardinality;
    }

    public finishTarget (variables : Array<Variable<any>>) : void
    {
        if (this.variableName)
        {
            throw new Error("Variable name already parsed");
        }
        if (
            this.content.length !== 1
            || !(this.content[0] instanceof StringNode))
        {
            throw new Error("Variable name must be 1 string node!");
        }
        this.variableName = (this.content[0] as StringNode).value;
        const variable : Variable<any> = variables.find(
            (v : Variable<any>) => v.name === this.variableName);
        if (!variable)
        {
            throw new Error(`Unknown variable ${this.variableName}`);
        }
        this.type = variable.type;
        this.cardinality = variable.cardinality;
    }

    public returnType () : DataType
    {
        return this.type;
    }

    public eject () : Interpolator<Variable<any>>
    {
        return (variables : Array<Variable<any>>) =>
            variables.find((v : Variable<any>) => v.name === this.variableName);
    }
}

class DirectiveNode extends ParseNode
{
    public directive : AnyDirective;
    public parameters : ParseNode[] = [];
    public target : ParseNode;
    constructor (
        initialTarget? : ParseNode,
        variables? : Array<Variable<any>>,
        // initialParams? : ParseNode[],
    ) {
        super();
        if (initialTarget)
        {
            this.content.push(initialTarget);
            this.finishTarget(variables);
            // this.parameters = initialParams;
        }
    }
    public returnType () : DataType
    {
        return this.directive.returnType();
    }
    public finishTarget (variables : Array<Variable<any>>) : void
    {
        if (this.content.length !== 1)
        {
            throw new Error(`Target must be exactly 1 node`);
        }
        this.target = this.content.pop();
    }
    public finishParameter () : boolean
    {
        if (this.content.length === 0)
        {
            this.parameters.push(new StringNode(""));
        }
        else if (this.content.length === 1)
        {
            this.parameters.push(this.content[0]);
        }
        else
        {
            const cNode : ConcatNode = new ConcatNode();
            this.content.forEach((node : ParseNode) => cNode.add(node));
            this.parameters.push(cNode);
        }
        this.content = [];
        return true;
    }
    public setDirective (name : string, required : boolean = false) : boolean
    {
        const directiveIndex : DirectiveEntry = Directives.get(name);
        if (!directiveIndex)
        {
            // There must be entries for all valid segments, even if it's a
            // directive that always requires subdirectives (this is expressed
            // by an entry with terminates: false and hasSubdirectives: true).
            throw new Error(`Unknown directive ${name}`);
        }

        const type : DataType = this.target.returnType();
        if (directiveIndex.terminates)
        {
            // we might be allowed to stop here
            const DC : DirectiveClass =
                directiveIndex.byType(type);
            if (DC)
            {
                this.directive = new DC();
                return true;
            }
        }
        // If we reached this point, we didn't match a directive

        if (required || !directiveIndex.hasSubdirectives)
        {
            // Either we are required to stop or there are no more children
            throw new Error(
                `Could not find directive ${name} for type ${type}`);
        }
        else
        {
            // We still have more opportunities to match, so signal the parser
            return false;
        }
    }
    public finish () : void
    {
        const paramTypes : DataType[] = this.parameters.map(
            (node : ParseNode) => node.returnType());
        const cardinalities : number[] = this.parameters.map(
            (node : ParseNode) => node.returnCardinality());
        this.directive.verifyParameters(
            paramTypes,
            this.target.returnType(),
            cardinalities
        );
    }
    public eject () : Interpolator<Variable<any>>
    {
        return (variables : Array<Variable<any>>) => {
            const params : Array<Variable<any>> =
                this.parameters.map((p : ParseNode) =>
                    p.eject()(variables));
            const targetType : DataType = this.target.returnType();
            return wrap(
                this.directive.returnType(),
                this.directive.execute(
                    this.directive.parseTarget(this.target.eject()(variables)),
                    this.directive.parseParameters(params)
                )
            );
        };
    }
}

class ParseRoot extends ParseNode
{
    public finishParameter () : boolean
    {
        return false;
    }
    public returnType () : DataType
    {
        // Strictly speaking, this method will probably never be called.
        return "string";
        // TODO: switch to HTML type?
        // Should wait for compiler distinction -- we never want the client to
        // have stricter types (unless it's based on information the compiler
        // couldn't know) because it introduces runtime failures.
    }
    public eject () : Interpolator<Variable<"string">>
    {
        throw new Error(`Use ejectRoot() instead of eject()!`);
    }
    public ejectRoot () : Interpolator<string>
    {
        return ((variables : Array<Variable<any>>) =>
            this.content.map((n : ParseNode) =>
                DefaultDirective.execute(n.eject()(variables))
            ).join("")
        );
    }
}

class ConcatNode extends ParseNode
{
    public returnType () : DataType
    {
        if (this.content.length === 1)
        {
            // this node might get collapsed
            return this.content[0].returnType();
        }
        return "string";
    }

    public eject () : Interpolator<Variable<any>>
    {
        if (this.content.length === 0)
        {
            return () => wrap("string", "");
        }
        if (this.content.length === 1)
        {
            return this.content[0].eject();
        }
        return ((variables : Array<Variable<any>>) =>
            wrap(
                "string",
                this.content.map((n : ParseNode) =>
                    DefaultDirective.execute(n.eject()(variables))
                ).join(""))
            );
    }
}

/**
 * Convenience set of the metacharacters understood by the template parser.
 */
const METACHARACTERS : Set<string> = new Set(["`", "$", "{", "}", "|", ","]);

class StringAccumulator
{
    public value : string = "";
    public add (char : string) : void
    {
        if (char.length !== 1)
        {
            throw new Error("Can only accumulate 1 character at a time!");
        }
        this.value += char;
    }
    public flush () : string
    {
        const a : string = this.value;
        this.value = "";
        return a;
    }
}

/**
 * Parse a template string and return an interpolator function that can be run
 * on the state variables to produce the final rendered output.
 * @param template The template string to parse
 */
// tslint:disable-next-line:cyclomatic-complexity
export function buildInterpolator (
    template : string,
    variables : Array<Variable<any>>
) : Interpolator<string>
{
    // Shortcut: if the template has no unescaped opening braces, there couldn't
    // be any references or directives, so just unescape ` sequences and return
    // a trivial function that outputs that string.
    if (!template.match(/(^|[^`])\{/))
    {
        const unescaped : string = template.replace(/`([`${|},])/, "$1");
        return () => unescaped;
    }

    const root : ParseRoot = new ParseRoot();
    let node : ParseNode = root;
    const acc : StringAccumulator = new StringAccumulator();
    let state : TextState = TextState.Normal;
    let prev : TextState = null;
    const path : ParseNode[] = [];

    for (const char of template)
    {
        if (!node)
        {
            throw new Error(`Shouldn't happen: no current node!`);
        }
        switch (state)
        {
            case (TextState.SawTick): // node is any (except reference)
                if (METACHARACTERS.has(char))
                {
                    acc.add(char);
                    state = prev;
                    continue;
                }
                else
                {
                    throw new Error(`Unknown metacharacter (${char})`);
                }
            case (TextState.Saw$): // node is any (except reference)
                if (char === "{")
                {
                    state = TextState.VariableName;
                    path.push(node);
                    node = node.add(new ReferenceNode());
                    continue;
                }
                else
                {
                    throw new Error(`Illegal char sequence: saw $ without {`);
                }
            case (TextState.VariableName): // node should be reference
                if (char === "`")
                {
                    prev = state;
                    state = TextState.SawTick;
                    continue;
                }
                if (char === "|")
                {
                    node.addString(acc.flush());
                    node.finishTarget(variables); // set the variable name
                    node.finish();

                    node = path.slice(-1)[0].replaceLast(
                        new DirectiveNode(node, variables));
                    state = TextState.DirectiveName;
                    continue;
                }
                if (char === "}")
                {
                    node.addString(acc.flush());
                    node.finishTarget(variables);
                    node.finish();

                    state = TextState.Normal;
                    node = path.pop();
                    continue;
                }
                if (METACHARACTERS.has(char))
                {
                    throw new Error(`Unexpected metacharacter (${char})`);
                }
                break;
            case (TextState.DirectiveName): // node should be directive
                if (char === "`")
                {
                    prev = state;
                    state = TextState.SawTick;
                    continue;
                }
                if (char === "|")
                {
                    node.setDirective(acc.flush(), true);
                    node.finish();

                    node = path.slice(-1)[0].replaceLast(
                        new DirectiveNode(node, variables));
                    // state = TextState.DirectiveName; // (already true)
                    continue;
                }
                if (char === ",")
                {
                    if (node.setDirective(acc.value, false))
                    {
                        acc.flush();

                        // We've moved on to parameters
                        state = TextState.Normal;
                        continue;
                    }
                    else
                    {
                        // We did not find a matching directive name, but there
                        // may still be a matching subdirective. NOTE: This
                        // adds the comma to the partial string, which is
                        // deliberate to provide a convenient standardized, but
                        // otherwise-untypeable lookup for subdirectives
                        break;
                    }
                }
                if (char === "}")
                {
                    node.setDirective(acc.flush(), true);
                    node.finish();

                    node = path.pop();
                    state = TextState.Normal;
                    continue;
                }
                if (METACHARACTERS.has(char))
                {
                    throw new Error(`Directive contains dynamic reference!`);
                }
                break;

            case (TextState.Normal): // node should be root or directive
                if (char === "`")
                {
                    prev = state;
                    state = TextState.SawTick;
                    continue;
                }
                if (char === "$")
                {
                    node.addString(acc.flush());

                    prev = state;
                    state = TextState.Saw$;
                    continue;
                }
                if (char === "{")
                {
                    node.addString(acc.flush());

                    path.push(node);
                    node = node.add(new ConcatNode());

                    // We're now in a string expression, so continue in "normal"
                    // state = TextState.Normal
                    continue;
                }
                if (char === "|")
                {
                    if (node instanceof DirectiveNode)
                    {
                        // We're reading a parameter and need to close the
                        // directive and wrap it in a new one.
                        node.addString(acc.flush());
                        node.finishParameter();
                        node.finish();

                        node = path.slice(-1)[0].replaceLast(
                            new DirectiveNode(node, variables));
                        state = TextState.DirectiveName;
                        continue;
                    }
                    else if (node instanceof ConcatNode)
                    {
                        // We're in a string expression and hitting our first
                        // directive.
                        node.addString(acc.flush());
                        node.finish();

                        node = path.slice(-1)[0].replaceLast(
                            new DirectiveNode(node, variables));
                        state = TextState.DirectiveName;
                        continue;
                    }
                    else if (node instanceof ParseRoot)
                    {
                        // Allow literal pipes in root node
                        break;
                    }
                    else
                    {
                        throw new Error(`Shouldn't happen: bad node type`);
                    }
                }
                if (char === "}")
                {
                    if (node instanceof DirectiveNode)
                    {
                        node.addString(acc.flush());
                        node.finishParameter();
                        node.finish();

                        node = path.pop();
                        continue;
                    }
                    else if (node instanceof ConcatNode)
                    {
                        throw new Error(`String expression has no directives!`);
                    }
                    else if (node instanceof ParseRoot)
                    {
                        // Allow literal closing braces in root node
                        break;
                    }
                    else
                    {
                        throw new Error(`Shouldn't happen: bad node type`);
                    }
                }
                if (char === ",")
                {
                    node.addString(acc.flush());
                    if (node.finishParameter())
                    {
                        // The comma triggered a segment action (separation of
                        // parameters in a directive) so do nothing.
                        continue;
                    }
                    else
                    {
                        // Otherwise treat the comma as a literal character
                        break;
                    }
                }
                break;
            default:
                throw new Error(`Unknown parser state ${state}`);
        }
        // All break cases fall through to here, the standard case of adding a
        // character to the string accumulator.
        acc.add(char);
    }

    if (path.length > 0)
    {
        throw new Error(`Unclosed interpolation site!`);
    }
    if (node !== root)
    {
        throw new Error(`Shouldn't happen: current did not return to root`);
    }
    // Append any final suffix
    root.addString(acc.flush());

    console.warn(template, variables, root); // TODO: temp
    return root.ejectRoot();
}

// const TEST_SUITE : Array<[string, StdVariable[]]> = [
//     ["My {text|inflect,possessive} form is {allcaps|uppercase}", []]
// ];

// TEST_SUITE.forEach(
//     ([s, v] : [string, StdVariable[]]) =>
//         console.log(
//             (buildInterpolator(s, v)(v))
//         ));

export function stringAsVariable (str : string) : StdVariable
{
    return {
        name: "<raw input>",
        value: str,
        type: "string",
        collectionType: "none",
        cardinality: 1
    };
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/server/validation/interpolation.ts